import { bannerImagesSection } from '../../shared/components/avatar-banner/banner-images-editor.component';
import { Inject, Injectable } from '@angular/core';
import { DwMetaDataFormPreferences, DwMenuActionType, DwMetaDataRoutingService, DwModalResult, DwOrmDataService, DwOrmDataServiceToken,
    DwSecurityUserService, DwUiConfigRegistryService } from '@devwareapps/devware-cap';
import { Observable, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AppMetaData, DwUserEntity, UserProfileEntity } from '../../../meta-data/app-meta-data.service';
import { createProfileEditPreferences, createProfileStylePreferences, ProfileStyleIdentity } from '../user-profile.config';

/** Own-profile edit/style modals use CAP data ownership and save/cancel handling. */
@Injectable({ providedIn: 'root' })
export class ProfileFormService {
    constructor(private routing: DwMetaDataRoutingService, private registry: DwUiConfigRegistryService,
        @Inject(DwOrmDataServiceToken) private orm: DwOrmDataService, private security: DwSecurityUserService) {
        registry.registerConfig(AppMetaData.DwUser.ItemDetail.itemName + '-participant-profile-edit', createProfileEditPreferences());
        registry.registerConfig(AppMetaData.DwUser.ItemDetail.itemName + '-participant-profile-images', Object.assign(new DwMetaDataFormPreferences(), {hidePrimaryKey: true, section: bannerImagesSection(false)}));
        registry.registerConfig(AppMetaData.UserProfile.ItemDetail.itemName + '-participant-profile-style', createProfileStylePreferences());
    }
    isOwner(userId: number): boolean {
        return userId > 0 && this.security.securityContext?.ApplicationUser?.UserId === userId;
    }
    canEdit(userId: number): boolean {
        return userId > 0 && (this.isOwner(userId) || this.security.hasPermission('Streaker_Profile_CanAdminAll') || this.security.hasPermission('Ent_AdminCanAccess'));
    }
    loadOwnedProfile(userId: number): Observable<UserProfileEntity> {
        if (!this.canEdit(userId)) { return throwError(new Error('You can only edit your own profile.')); }
        const query = AppMetaData.UserProfile.CreateQueryBuilder().addFilterAnd(f => f.Equal(a => a.UserId, userId));
        return this.orm.executeQuery<UserProfileEntity>(query).pipe(take(1), map(rows => rows.length === 1 ? rows[0] : null));
    }
    edit(userId: number): Observable<DwModalResult<DwUserEntity>> {
        if (!this.canEdit(userId)) { return throwError(new Error('You can only edit your own profile.')); }
        return this.routing.openAddEditViewModal(DwMenuActionType.edit, AppMetaData.DwUser.ItemDetail.itemName,
            userId, null, null, 'participant-profile-edit', { title: 'Edit profile' }, true);
    }
    images(userId: number): Observable<DwModalResult<DwUserEntity>> {
        if (!this.canEdit(userId)) { return throwError(new Error('You can only edit your own images.')); }
        return this.routing.openAddEditViewModal(DwMenuActionType.edit, AppMetaData.DwUser.ItemDetail.itemName,
            userId, null, null, 'participant-profile-images', {title: 'Profile images'}, true);
    }
    saveImages(userId: number, avatarId: number, bannerId: number): Observable<void> {
        if (!this.canEdit(userId)) { return throwError(new Error('You can only edit your own images.')); }
        const entity = AppMetaData.DwUser.CreateEntity();
        entity.UserId = userId;
        entity.UserPictureMediaId = avatarId;
        entity.UserBackgroundMediaId = bannerId;
        return this.orm.saveEntity(entity, false).pipe(map(() => undefined));
    }
    style(profile: UserProfileEntity, identity?: ProfileStyleIdentity): Observable<DwModalResult<UserProfileEntity>> {
        if (!profile?.UserProfileId || !this.canEdit(profile.UserId)) { return throwError(new Error('You can only edit your own style.')); }
        this.registry.registerConfig(AppMetaData.UserProfile.ItemDetail.itemName + '-participant-profile-style', createProfileStylePreferences(identity));
        return this.routing.openAddEditViewModal(DwMenuActionType.edit, AppMetaData.UserProfile.ItemDetail.itemName,
            profile.UserProfileId, null, null, 'participant-profile-style', { title: 'Profile style' }, true);
    }
}
